import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { formatINR } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LineChart, Line } from "recharts";

function heatColor(v, max) {
  if (!v) return "#F5F5F5";
  const t = Math.min(1, v / Math.max(1, max));
  // Blend from light lime → Klein Blue
  const c1 = [212, 255, 0]; // #D4FF00
  const c2 = [0, 47, 167];  // #002FA7
  const mix = c1.map((a, i) => Math.round(a + (c2[i] - a) * t));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

function shortWeek(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [heat, setHeat] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data));
    api.get("/analytics/heatmap").then((r) => setHeat(r.data));
  }, []);

  const utilRows = heat ? heat.rows.map((r) => ({ name: r.name, total: r.cells.reduce((a, b) => a + b, 0) })).sort((a, b) => b.total - a.total).slice(0, 10) : [];

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Section", "Key", "Value"]];
    data.payment_trend.forEach((p) => rows.push(["payment_trend", p.month, `paid=${p.paid};pending=${p.pending}`]));
    data.work_order_status.forEach((p) => rows.push(["work_order_status", p.status, p.count]));
    data.top_performers.forEach((p) => rows.push(["top_performers", p.name, `q=${p.avg_quality};ot=${p.avg_on_time}`]));
    if (heat) heat.rows.forEach((r) => rows.push(["utilization_heatmap", r.name, r.cells.join(";")]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "analytics.csv"; a.click();
  };

  if (!data) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  const heatMax = heat ? Math.max(1, ...heat.rows.flatMap((r) => r.cells)) : 1;

  return (
    <div>
      <PageHeader
        title="Analytics"
        description="Utilization, payment trends and top performers."
        actions={<button data-testid="export-analytics-btn" onClick={exportCsv} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">Export CSV</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-neutral-200 bg-white lg:col-span-2" data-testid="chart-utilization-compact">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Writer Utilization — last 8 weeks (top 10)</div>
          <div className="p-4">
            {utilRows.length === 0 ? (
              <div className="text-sm text-neutral-500 py-6 text-center">No work orders in the last 8 weeks — assign work to see utilization.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, utilRows.length * 32)}>
                <BarChart data={utilRows} layout="vertical" margin={{ left: 60, right: 24 }}>
                  <CartesianGrid stroke="#E5E5E5" />
                  <XAxis type="number" stroke="#0A0A0A" fontSize={11} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" stroke="#0A0A0A" fontSize={11} width={120} />
                  <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 8, background: "#fff" }} />
                  <Bar dataKey="total" fill="#1F6B45" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-neutral-200 bg-white p-4" data-testid="chart-payment-trend">
          <div className="label-caps mb-3">Payment Trend</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.payment_trend}>
              <CartesianGrid stroke="#E5E5E5" strokeDasharray="0" />
              <XAxis dataKey="month" stroke="#0A0A0A" fontSize={11} />
              <YAxis stroke="#0A0A0A" fontSize={11} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, background: "#fff" }} formatter={(v) => formatINR(v)} />
              <Line type="stepAfter" dataKey="paid" stroke="#002FA7" strokeWidth={3} dot={false} />
              <Line type="stepAfter" dataKey="pending" stroke="#FF2A00" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-neutral-200 bg-white p-4" data-testid="chart-work-orders">
          <div className="label-caps mb-3">Work Orders by Status</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.work_order_status}>
              <CartesianGrid stroke="#E5E5E5" strokeDasharray="0" />
              <XAxis dataKey="status" stroke="#0A0A0A" fontSize={11} />
              <YAxis stroke="#0A0A0A" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, background: "#fff" }} />
              <Bar dataKey="count" fill="#0A0A0A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-neutral-200 bg-white p-4" data-testid="chart-utilization">
          <div className="label-caps mb-3">Overall Utilization (Completed / Assigned)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.utilization} layout="vertical">
              <CartesianGrid stroke="#E5E5E5" />
              <XAxis type="number" stroke="#0A0A0A" fontSize={11} unit="%" />
              <YAxis dataKey="name" type="category" stroke="#0A0A0A" fontSize={11} width={110} />
              <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, background: "#fff" }} />
              <Bar dataKey="utilization" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-neutral-200 bg-white" data-testid="chart-top-performers">
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">Top Performers</div>
          <table className="fms-table w-full">
            <thead><tr><th>#</th><th>Name</th><th>Quality</th><th>On-Time</th><th>Reviews</th></tr></thead>
            <tbody>
              {data.top_performers.map((p, i) => (
                <tr key={p.freelancer_id}>
                  <td className="font-mono-tight text-neutral-500">{String(i + 1).padStart(2, "0")}</td>
                  <td>{p.name}</td>
                  <td className="font-mono-tight">{p.avg_quality}</td>
                  <td className="font-mono-tight">{p.avg_on_time}</td>
                  <td className="font-mono-tight">{p.reviews}</td>
                </tr>
              ))}
              {data.top_performers.length === 0 && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No reviews yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
